/**
 * TanStack Query hooks for Analytics API
 * All hooks require admin role and valid authentication
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { analyticsApiService } from '@/services/api/analytics.service';
import type {
  AnalyticsDateRangeParams,
  MetricsQueryParams,
  MetricsResponse,
  VoicebotQualityResponse,
  ChatbotQualityResponse,
  GlobalSummaryResponse,
} from '@/types/analytics';

/**
 * Hook to fetch aggregated metrics (voicebot & chatbot)
 *
 * @param params - Date range and optional filters
 * @returns Query result with metrics data, loading, and error states
 *
 * Features:
 * - 5-minute stale time (data refreshes every 5 minutes)
 * - Enabled only for authenticated admin users
 * - Automatic retry on failure (2 attempts)
 * - No refetch on window focus (avoid unnecessary API calls)
 */
export const useMetrics = (params: MetricsQueryParams) => {
  const { isAuthenticated, user } = useAuth();

  return useQuery<MetricsResponse>({
    queryKey: ['analytics', 'metrics', params],
    queryFn: () => analyticsApiService.getMetrics(params),
    enabled: isAuthenticated && user?.role === 'admin',
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch voicebot quality analysis
 *
 * @param params - Date range and optional client filter
 * @returns Query result with voicebot quality data
 *
 * Data Source: AI-generated analysis from voicebot_analysis table
 * Updated: Weekly (Sundays at 3:00 AM by Mastra workflow)
 */
export const useVoicebotQuality = (params: AnalyticsDateRangeParams) => {
  const { isAuthenticated, user } = useAuth();

  return useQuery<VoicebotQualityResponse>({
    queryKey: ['analytics', 'voicebot', 'quality', params],
    queryFn: () => analyticsApiService.getVoicebotQuality(params),
    enabled: isAuthenticated && user?.role === 'admin',
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch chatbot quality analysis
 *
 * @param params - Date range and optional client filter
 * @returns Query result with chatbot quality data
 *
 * Data Source: AI-generated analysis from chatbot_analysis table
 * Updated: Weekly (Sundays at 3:00 AM by Mastra workflow)
 */
export const useChatbotQuality = (params: AnalyticsDateRangeParams) => {
  const { isAuthenticated, user } = useAuth();

  return useQuery<ChatbotQualityResponse>({
    queryKey: ['analytics', 'chatbot', 'quality', params],
    queryFn: () => analyticsApiService.getChatbotQuality(params),
    enabled: isAuthenticated && user?.role === 'admin',
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch global analytics summary
 *
 * @param params - Date range and optional client filter
 * @returns Query result with consolidated summary data
 *
 * Use Case: Primary hook for admin dashboard overview
 * Implementation: Backend uses Promise.all() for parallel queries (<50ms target)
 * Contains: Voicebot metrics, chatbot metrics, quality scores, combined KPIs
 */
export const useAnalyticsSummary = (params: AnalyticsDateRangeParams) => {
  const { isAuthenticated, user } = useAuth();

  return useQuery<GlobalSummaryResponse>({
    queryKey: ['analytics', 'summary', params],
    queryFn: () => analyticsApiService.getSummary(params),
    enabled: isAuthenticated && user?.role === 'admin',
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

/**
 * Utility function to generate ISO 8601 date range from preset
 *
 * @param preset - One of '7d', '30d', '90d'
 * @returns Object with startDate and endDate in ISO 8601 format
 *
 * Example:
 * ```ts
 * const { startDate, endDate } = getDateRangeFromPreset('7d');
 * // Returns last 7 days from now
 * ```
 */
export const getDateRangeFromPreset = (preset: '7d' | '30d' | '90d'): { startDate: string; endDate: string } => {
  const endDate = new Date();
  const startDate = new Date();

  switch (preset) {
    case '7d':
      startDate.setDate(startDate.getDate() - 7);
      break;
    case '30d':
      startDate.setDate(startDate.getDate() - 30);
      break;
    case '90d':
      startDate.setDate(startDate.getDate() - 90);
      break;
  }

  // Set time to start/end of day for cleaner queries
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
};

/**
 * Utility function to format date for display
 *
 * @param isoDate - ISO 8601 date string
 * @returns Formatted date string (e.g., "Feb 1, 2025")
 */
export const formatAnalyticsDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Utility function to calculate days between two dates
 *
 * @param startDate - ISO 8601 start date
 * @param endDate - ISO 8601 end date
 * @returns Number of days in the range
 */
export const calculateDaysBetween = (startDate: string, endDate: string): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};
